import { useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { apiFetch, ApiClientError, clearTokens } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/Button';
import { PasswordSetInput, isPasswordSetValid } from '@/components/PasswordSetInput';
import misesparkSymbol from '@/assets/logo/misespark-symbol-full-light.svg';

// v1.0 招待リンク方式 (docs/design/v10-multi-tenant.md Chapter 8.8): システム管理者が
// POST /platform/tenants/:tenantId/users で発行した setup_token を受け取り、
// POST /auth/setup-password で初回パスワードを設定する。LoginPage.tsx と同じカードレイアウトで統一。
// Security指摘 (HIGH): クエリパラメータはブラウザ履歴・サーバー/CDNログ・Refererヘッダーに平文で残るため、
// URLフラグメント (#token=...、ブラウザ内限定でサーバーには送出されない) で受け渡す方式にしている。
const getTokenFromHash = (): string => {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return params.get('token') ?? '';
};

/**
 * v0.4 追加要件⑲ D-495: 招待の初回設定とセルフリセットで**引き換えエンドポイントを分けない**
 * (分けると TOCTOU 対策の条件付き UPDATE・監査・ハッシュ化が二重化し §8-55 型の同期漏れを新規に作る)。
 * 実際の振る舞いの差 (locked の解除) はサーバー側で `setup_tokens.purpose` を見て分岐しており、
 * この画面が担うのは**文言の出し分けだけ**である。
 *
 * ⚠️ §8-95: 同一コンポーネントを prop 違いで複数 route に割り当てると React がインスタンスを
 * 再利用するため、prop 由来の値を `useState` の初期値にすると route 間で持ち越される。
 * `mode` は描画にのみ使い、`useState` の初期値には決して渡さないこと。
 */
export type PasswordSetupMode = 'invite' | 'reset';

const MODE_COPY: Record<PasswordSetupMode, {
  heading: string; completed: string; invalid: string;
  /** ⚠️ 期限切れ・無効時の復旧導線。reset は TTL 60分のため「期限切れ」が最も頻度の高い失敗であり、
   *  ここにリンクが無いと利用者が行き止まりになる (招待は24h + 管理者に依頼で閉じるため元設計で成立していた)。 */
  recovery: { to: string; label: string } | null;
}> = {
  invite: {
    heading: '初回パスワードの設定',
    completed: 'パスワードを設定しました。ログイン画面に移動します…',
    invalid: 'このリンクは無効です。管理者に再発行を依頼してください',
    recovery: null,
  },
  reset: {
    heading: 'パスワードの再設定',
    completed: 'パスワードを再設定しました。ログイン画面に移動します…',
    invalid: 'このリンクは無効か、有効期限 (60分) が切れています。もう一度発行してください',
    recovery: { to: '/forgot-password', label: '再設定をやり直す' },
  },
};

export function SetupPasswordPage({ mode = 'invite' }: { mode?: PasswordSetupMode } = {}) {
  const copy = MODE_COPY[mode];
  const [token] = useState(getTokenFromHash);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);

  // 招待リンクは「別アカウントとしてセットアップする」フローのため、このブラウザに他テナント/他ユーザーの
  // セッションが残っていても即座に破棄する (App.tsxのroute guardをuser非依存にしたのとセットの対応。
  // 破棄しないと setup 完了後の /login 遷移時にも古いセッションが有効なままとなり、招待されたテナントで
  // ログインできない)
  useEffect(() => {
    clearTokens();
    setUser(null);
  }, [setUser]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!isPasswordSetValid(newPassword, confirmPassword)) {
      setError(newPassword.length < 8 ? 'パスワードは8文字以上で入力してください' : 'パスワードと確認用が一致しません');
      return;
    }
    setLoading(true);
    try {
      await apiFetch<{ completed: boolean }>('/auth/setup-password', {
        method: 'POST',
        body: JSON.stringify({ token, new_password: newPassword }),
      });
      setCompleted(true);
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      if (err instanceof ApiClientError && err.code.startsWith('SETUP_TOKEN_')) {
        setError(copy.invalid);
      } else if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('通信エラーが発生しました');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-canvas px-4">
      <div className="w-full max-w-md">
        <div className="bg-surface-card rounded-xl shadow-overlay p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 justify-center mb-2">
              <img src={misesparkSymbol} alt="" className="w-12 h-12" />
            </div>
            <h1 className="text-[26px] font-extrabold tracking-tight">
              <span className="text-misespark-navy">Mise</span>
              <span className="bg-gradient-to-r from-misespark-orange to-misespark-yellow bg-clip-text text-transparent">Spark</span>
            </h1>
            <p className="text-sm text-text-sub mt-1">{copy.heading}</p>
          </div>

          {!token && (
            <div
              role="alert"
              className="mb-4 p-3 rounded-md bg-danger-bg text-danger-text text-[13px] border border-danger-border flex items-start gap-2"
            >
              <AlertCircle size={16} className="shrink-0 mt-0.5" aria-hidden />
              <span>{copy.invalid}</span>
            </div>
          )}

          {/* ⚠️ 復旧導線。reset は TTL 60分のため「期限切れ」が最も頻度の高い失敗であり、
              リンクが無いとここが行き止まりになる (design-expert Critical)。
              招待 (invite) は「管理者に再発行を依頼」で閉じるため recovery: null。 */}
          {!token && (
            <RecoveryLinks recovery={copy.recovery} />
          )}

          {token && completed && (
            <div
              role="status"
              className="mb-4 p-3 rounded-md bg-success-bg text-success-text text-[13px] border border-success-border flex items-start gap-2"
            >
              <CheckCircle2 size={16} className="shrink-0 mt-0.5" aria-hidden />
              <span>{copy.completed}</span>
            </div>
          )}

          {token && !completed && (
            <>
              {error && (
                <div
                  role="alert"
                  className="mb-4 p-3 rounded-md bg-danger-bg text-danger-text text-[13px] border border-danger-border flex items-start gap-2"
                >
                  <AlertCircle size={16} className="shrink-0 mt-0.5" aria-hidden />
                  <span>{error}</span>
                </div>
              )}
              {error === copy.invalid && <RecoveryLinks recovery={copy.recovery} />}

              <form onSubmit={onSubmit} className="space-y-4">
                <PasswordSetInput
                  idPrefix="setup"
                  value={newPassword}
                  confirmValue={confirmPassword}
                  onChange={setNewPassword}
                  onConfirmChange={setConfirmPassword}
                  label="新しいパスワード（8文字以上）"
                  confirmLabel="新しいパスワード（確認用）"
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  className="w-full"
                  disabled={!isPasswordSetValid(newPassword, confirmPassword)}
                  loading={loading}
                  loadingLabel="設定中…"
                >
                  パスワードを設定する
                </Button>
              </form>
            </>
          )}
        </div>
        <p className="text-center text-xs text-text-muted mt-4">
          © 2026 MiseSpark
        </p>
      </div>
    </div>
  );
}

/** 無効・期限切れ時の復旧導線。⚠️ ここが無いと reset の最頻の失敗 (期限切れ) で行き止まりになる。 */
function RecoveryLinks({ recovery }: { recovery: { to: string; label: string } | null }) {
  return (
    <div className="mb-4 flex flex-col items-center gap-1">
      {recovery && (
        <Link
          to={recovery.to}
          className="text-[13px] text-chrome-base underline underline-offset-2 inline-flex items-center min-h-[44px] px-2"
        >
          {recovery.label}
        </Link>
      )}
      <Link
        to="/login"
        className="text-[12px] text-chrome-base underline underline-offset-2 inline-flex items-center min-h-[44px] px-2"
      >
        ログイン画面へ
      </Link>
    </div>
  );
}
